import { createApi } from "@reduxjs/toolkit/query/react";
import { baseQuery } from "./baseQuery";

export const ordersApi = createApi({
  reducerPath: "ordersApi",
  baseQuery,
  tagTypes: ["Orders", "Order"],

  endpoints: (builder) => ({
    searchOrderItems: builder.query({
      query: (params = {}) => {
        const searchParams = new URLSearchParams();
        Object.entries(params || {}).forEach(([key, value]) => {
          if (value === undefined || value === null || value === "") {
            return;
          }

          searchParams.set(key, String(value));
        });

        const queryString = searchParams.toString();
        return queryString ? `orders/items/search?${queryString}` : "orders/items/search";
      },
      transformResponse: (res) => (res?.success ? res.data?.items || [] : []),
    }),

    fetchOrders: builder.query({
      query: (params = {}) => {
        const searchParams = new URLSearchParams();
        Object.entries(params || {}).forEach(([key, value]) => {
          if (value === undefined || value === null || value === "") {
            return;
          }

          searchParams.set(key, String(value));
        });

        const queryString = searchParams.toString();
        return queryString ? `orders?${queryString}` : "orders";
      },
      transformResponse: (res) => (res?.success ? res.data : res ?? null),
      providesTags: (result) =>
        result?.items
          ? [
              ...result.items.map((order) => ({ type: "Order", id: order.id })),
              { type: "Orders", id: "LIST" },
            ]
          : [{ type: "Orders", id: "LIST" }],
    }),

    fetchOrderById: builder.query({
      query: (orderId) => `orders/${orderId}`,
      transformResponse: (res) => (res?.success ? res.data?.order || null : res?.order || null),
      providesTags: (_result, _error, orderId) => [{ type: "Order", id: orderId }],
    }),

    createOrder: builder.mutation({
      query: (orderData) => ({
        url: "orders/create",
        method: "POST",
        body: orderData,
      }),
      transformResponse: (res) => {
        if (res?.success && res?.data) {
          return res.data;
        }
        return res;
      },
      invalidatesTags: [{ type: "Orders", id: "LIST" }],
    }),

    updateOrder: builder.mutation({
      query: ({ orderId, ...body }) => ({
        url: `orders/${orderId}`,
        method: "PATCH",
        body,
      }),
      transformResponse: (res) =>
        res?.success ? { ...(res.data || {}), message: res.message } : res ?? null,
      invalidatesTags: (_result, _error, arg) => [
        { type: "Order", id: arg.orderId },
        { type: "Orders", id: "LIST" },
      ],
    }),

    reopenOrder: builder.mutation({
      query: (orderId) => ({
        url: `orders/${orderId}/reopen`,
        method: "POST",
      }),
      transformResponse: (res) =>
        res?.success ? { ...(res.data || {}), message: res.message } : res ?? null,
      invalidatesTags: (_result, _error, orderId) => [
        { type: "Order", id: orderId },
        { type: "Orders", id: "LIST" },
      ],
    }),

    markOrderDelivered: builder.mutation({
      query: (orderId) => ({
        url: `orders/${orderId}/deliver`,
        method: "POST",
      }),
      transformResponse: (res) =>
        res?.success ? { ...(res.data || {}), message: res.message } : res ?? null,
      invalidatesTags: (_result, _error, orderId) => [
        { type: "Order", id: orderId },
        { type: "Orders", id: "LIST" },
      ],
    }),

    finalizeOrderSale: builder.mutation({
      query: (orderId) => ({
        url: `orders/${orderId}/finalize-sale`,
        method: "POST",
      }),
      transformResponse: (res) =>
        res?.success ? { ...(res.data || {}), message: res.message } : res ?? null,
      invalidatesTags: (_result, _error, orderId) => [
        { type: "Order", id: orderId },
        { type: "Orders", id: "LIST" },
      ],
    }),

    markOrderReturnedToSender: builder.mutation({
      query: ({ orderId, note }) => ({
        url: `orders/${orderId}/mark-returned-to-sender`,
        method: "POST",
        body: note ? { note } : {},
      }),
      transformResponse: (res) =>
        res?.success ? { ...(res.data || {}), message: res.message } : res ?? null,
      invalidatesTags: (_result, _error, arg) => [
        { type: "Order", id: arg.orderId },
        { type: "Orders", id: "LIST" },
      ],
    }),

    searchOrderCustomers: builder.query({
      query: (params = {}) => {
        const searchParams = new URLSearchParams();
        Object.entries(params || {}).forEach(([key, value]) => {
          if (value === undefined || value === null || value === "") {
            return;
          }

          searchParams.set(key, String(value));
        });

        return `orders/customers/search?${searchParams.toString()}`;
      },
      transformResponse: (res) => (res?.success ? res.data?.items || [] : []),
    }),

    fetchOrderCustomerHistory: builder.query({
      query: (customerId) => `orders/customers/${customerId}/history`,
      transformResponse: (res) => (res?.success ? res.data || null : null),
    }),

    resolveOrderItems: builder.mutation({
      query: (catalogVariantIds) => ({
        url: "orders/items/resolve",
        method: "POST",
        body: { catalogVariantIds },
      }),
      transformResponse: (res) => ({
        items: res?.success ? res.data?.items || [] : [],
        unresolved: res?.success ? res.data?.unresolved || [] : [],
      }),
    }),

    scanOrderItem: builder.mutation({
      query: (barcode) => ({
        url: "orders/items/scan",
        method: "POST",
        body: { barcode },
      }),
      transformResponse: (res) =>
        res?.success ? { ...(res.data || {}), message: res.message } : res ?? null,
    }),

    confirmStorefrontOrder: builder.mutation({
      query: (orderId) => ({
        url: `orders/${orderId}/confirm`,
        method: "POST",
      }),
      transformResponse: (res) =>
        res?.success ? { ...(res.data || {}), message: res.message } : res ?? null,
      invalidatesTags: (_result, _error, orderId) => [
        { type: "Order", id: orderId },
        { type: "Orders", id: "LIST" },
      ],
    }),

    cancelStorefrontOrder: builder.mutation({
      // Accepts `{ orderId, reason, note }`. A bare id is still accepted, but it is
      // never interpolated as an object: passing the object into the URL produced
      // `orders/[object Object]/cancel`, which the API rejected with a 400.
      query: (arg) => {
        const { orderId, reason, note } =
          arg !== null && typeof arg === "object" ? arg : { orderId: arg };

        return {
          url: `orders/${orderId}/cancel`,
          method: "POST",
          body: {
            ...(reason ? { reason } : {}),
            ...(note ? { note } : {}),
          },
        };
      },
      transformResponse: (res) =>
        res?.success ? { ...(res.data || {}), message: res.message } : res ?? null,
      invalidatesTags: (_result, _error, arg) => [
        { type: "Order", id: arg !== null && typeof arg === "object" ? arg.orderId : arg },
        { type: "Orders", id: "LIST" },
      ],
    }),

    requestOrderShipment: builder.mutation({
      query: (orderId) => ({
        url: `orders/${orderId}/request-shipment`,
        method: "POST",
      }),
      transformResponse: (res) =>
        res?.success ? { ...(res.data || {}), message: res.message } : res ?? null,
      invalidatesTags: (_result, _error, orderId) => [
        { type: "Order", id: orderId },
        { type: "Orders", id: "LIST" },
      ],
    }),

    // Emails the stored label to the store printer. Never creates a shipment.
    printOrderShippingLabel: builder.mutation({
      query: ({ orderId, confirmRepeat = false }) => ({
        url: `orders/${orderId}/shipping-label/print`,
        method: "POST",
        body: confirmRepeat ? { confirmRepeat: true } : {},
      }),
      transformResponse: (res) =>
        res?.success ? { ...(res.data || {}), message: res.message } : res ?? null,
      invalidatesTags: (_result, _error, arg) => [{ type: "Order", id: arg.orderId }],
    }),

    retryOrderShipment: builder.mutation({
      query: (orderId) => ({
        url: `orders/${orderId}/retry-shipment`,
        method: "POST",
      }),
      transformResponse: (res) =>
        res?.success ? { ...(res.data || {}), message: res.message } : res ?? null,
      invalidatesTags: (_result, _error, orderId) => [
        { type: "Order", id: orderId },
        { type: "Orders", id: "LIST" },
      ],
    }),

    refreshOrderShipmentStatus: builder.mutation({
      query: (orderId) => ({
        url: `orders/${orderId}/refresh-shipment-status`,
        method: "POST",
      }),
      transformResponse: (res) =>
        res?.success ? { ...(res.data || {}), message: res.message } : res ?? null,
      invalidatesTags: (_result, _error, orderId) => [
        { type: "Order", id: orderId },
        { type: "Orders", id: "LIST" },
      ],
    }),

    trackOrder: builder.query({
      query: (pod) => ({
        url: "orders/track",
        params: { pod },
      }),
      transformResponse: (res) => {
        if (res?.success && res?.data) {
          return res.data;
        }
        return res;
      },
      providesTags: (result, error, pod) => [
        { type: "Order", id: `pod-${pod}` },
      ],
    }),

    getOrderStatus: builder.query({
      query: (pod) => ({
        url: "orders/status",
        params: { pod },
      }),
      transformResponse: (res) => {
        if (res?.success && res?.data) {
          return res.data;
        }
        return res;
      },
      providesTags: (result, error, pod) => [
        { type: "Order", id: `pod-${pod}` },
      ],
    }),

    addOrderComment: builder.mutation({
      query: ({ pod, comment }) => ({
        url: "orders/comment",
        method: "POST",
        body: { pod, comment },
      }),
      transformResponse: (res) => {
        if (res?.success && res?.data) {
          return res.data;
        }
        return res;
      },
      invalidatesTags: (result, error, arg) => [
        { type: "Order", id: `pod-${arg.pod}` },
        { type: "Orders", id: "LIST" },
      ],
    }),
  }),
});

export const {
  useSearchOrderItemsQuery,
  useFetchOrdersQuery,
  useFetchOrderByIdQuery,
  useCreateOrderMutation,
  useUpdateOrderMutation,
  useReopenOrderMutation,
  useMarkOrderDeliveredMutation,
  useFinalizeOrderSaleMutation,
  useMarkOrderReturnedToSenderMutation,
  useSearchOrderCustomersQuery,
  useLazySearchOrderCustomersQuery,
  useFetchOrderCustomerHistoryQuery,
  useResolveOrderItemsMutation,
  useScanOrderItemMutation,
  useCancelStorefrontOrderMutation,
  useConfirmStorefrontOrderMutation,
  useRequestOrderShipmentMutation,
  usePrintOrderShippingLabelMutation,
  useRetryOrderShipmentMutation,
  useRefreshOrderShipmentStatusMutation,
  useTrackOrderQuery,
  useLazyTrackOrderQuery,
  useGetOrderStatusQuery,
  useLazyGetOrderStatusQuery,
  useAddOrderCommentMutation,
} = ordersApi;
